/**
 * Local seed data for the Communities feature — stands in for a real API.
 * Community names/tones mirror src/data.js's COMMUNITIES teaser (landing
 * page + home widget) so the whole app feels like one continuous world.
 */

const DAY = 24 * 60 * 60 * 1000;
const now = Date.now();

export const CURRENT_USER_ID = 'user_me';

export const CATEGORIES = [
  'All', 'Genres', 'Reading Clubs', 'Writing', 'Comics', 'Classics', 'Fandoms', 'Book Discussions',
];

export const DEFAULT_RULES = [
  'Be respectful — disagree with ideas, not people.',
  'No harassment, hate speech, or personal attacks.',
  'Use spoiler tags for anything beyond the first few chapters.',
  'Keep discussions relevant to the community.',
  'Respect writers and creators, including yourself.',
];

function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}
const M = (id, name, tone, role = 'member', extra = {}) => ({ id, name, initial: name[0], tone, role, booksRead: 8 + (hash(id + name) % 60), ...extra });

export const COMMUNITIES = [
  {
    id: 'fantasy-readers', name: 'Fantasy Readers', tone: 'coral', category: 'Genres', type: 'reader', visibility: 'public',
    tagline: 'Talk dragons, magic systems, unforgettable villains and the stories that keep us awake at 2 AM.',
    description: "For anyone who has ever dog-eared a map at the front of a book. We talk worldbuilding, magic systems, morally-grey villains, and which fantasy series ruined all others for us.",
    memberCount: 2400, activeNow: 186, createdAt: now - 400 * DAY,
    currentBook: { title: 'The Name of the Wind', author: 'Patrick Rothfuss', tone: 'coral' },
    tags: ['epic fantasy', 'worldbuilding', 'magic systems'],
    joinStatus: 'joined', role: 'member', unreadCount: 3,
  },
  {
    id: 'romance-readers', name: 'Romance Readers', tone: 'coral', category: 'Genres', type: 'reader', visibility: 'public',
    tagline: 'Slow burns, enemies to lovers, and the tropes that ruined us in the best way.',
    description: 'A cozy corner for swooning over slow burns, yelling about the third-act breakup, and trading recs by trope.',
    memberCount: 3100, activeNow: 241, createdAt: now - 500 * DAY,
    currentBook: { title: 'The Love Hypothesis', author: 'Ali Hazelwood', tone: 'coral' },
    tags: ['slow burn', 'contemporary romance'],
    joinStatus: 'none',
  },
  {
    id: 'mystery-crime', name: 'Mystery & Crime', tone: 'blue', category: 'Genres', type: 'reader', visibility: 'public',
    tagline: 'Whodunits, red herrings, and figuring out who actually did it before the reveal.',
    description: 'Detectives, cold cases, and unreliable narrators welcome. We keep spoilers tagged so latecomers can still play detective.',
    memberCount: 1800, activeNow: 94, createdAt: now - 320 * DAY,
    currentBook: { title: 'The Silent Patient', author: 'Alex Michaelides', tone: 'blue' },
    tags: ['whodunit', 'thriller'],
    joinStatus: 'none',
  },
  {
    id: 'manga-comics', name: 'Manga & Comics', tone: 'yellow', category: 'Comics', type: 'reader', visibility: 'public',
    tagline: "This week's chapter drop, panel breakdowns, and who's binge-reading what.",
    description: 'Weekly chapter discussions, art appreciation, and recommendations across manga, manhwa, and Western comics.',
    memberCount: 4200, activeNow: 312, createdAt: now - 610 * DAY,
    currentBook: { title: 'Chainsaw Man, Vol. 14', author: 'Tatsuki Fujimoto', tone: 'yellow' },
    tags: ['manga', 'manhwa', 'comics'],
    joinStatus: 'none',
  },
  {
    id: 'classic-literature', name: 'Classic Literature', tone: 'sage', category: 'Classics', type: 'reader', visibility: 'public',
    tagline: 'Why the ending still hurts, one hundred years later.',
    description: 'Reading the books that outlived their authors — from Austen to Baldwin — one close read at a time.',
    memberCount: 980, activeNow: 41, createdAt: now - 700 * DAY,
    currentBook: { title: 'Middlemarch', author: 'George Eliot', tone: 'sage' },
    tags: ['classics', 'close reading'],
    joinStatus: 'none',
  },
  {
    id: 'scifi-explorers', name: 'Sci-Fi Explorers', tone: 'blue', category: 'Genres', type: 'reader', visibility: 'public',
    tagline: "The physics, actually — and the futures we can't stop thinking about.",
    description: 'Hard sci-fi, space opera, and everything in between. Bring your favorite first-contact theory.',
    memberCount: 2000, activeNow: 128, createdAt: now - 380 * DAY,
    currentBook: { title: 'Project Hail Mary', author: 'Andy Weir', tone: 'sage' },
    tags: ['hard sci-fi', 'space opera'],
    joinStatus: 'joined', role: 'member', unreadCount: 1,
  },
  {
    id: 'young-adult', name: 'Young Adult Readers', tone: 'sage', category: 'Genres', type: 'reader', visibility: 'public',
    tagline: 'Coming-of-age stories, found family, and the books that got us through school.',
    description: 'For readers who never grew out of (and never will grow out of) a great YA story.',
    memberCount: 1560, activeNow: 77, createdAt: now - 260 * DAY,
    currentBook: { title: 'Six of Crows', author: 'Leigh Bardugo', tone: 'coral' },
    tags: ['coming of age', 'found family'],
    joinStatus: 'none',
  },
  {
    id: 'poetry-corner', name: 'Poetry Corner', tone: 'yellow', category: 'Writing', type: 'writer', visibility: 'public',
    tagline: 'One line that undid me — and the poems we keep coming back to.',
    description: 'A gentle space to read, write, and workshop poetry. All forms and skill levels welcome.',
    memberCount: 1100, activeNow: 52, createdAt: now - 300 * DAY,
    tags: ['poetry', 'workshop'],
    joinStatus: 'none',
  },
  {
    id: 'fiction-writers', name: "Fiction Writers", tone: 'coral', category: 'Writing', type: 'writer', visibility: 'restricted',
    tagline: 'Chapter one, take nine — a writers’ room for people actually finishing the draft.',
    description: 'A focused critique circle for fiction writers. Invite-only to keep feedback quality high and consistent.',
    memberCount: 2700, activeNow: 163, createdAt: now - 450 * DAY,
    tags: ['critique circle', 'drafting'],
    joinStatus: 'joined', role: 'moderator', unreadCount: 5,
  },
  {
    id: 'indie-writers', name: 'Indie Writers', tone: 'yellow', category: 'Writing', type: 'writer', visibility: 'private',
    tagline: 'Self-publishing, indie craft talk, and celebrating the wins that don’t come with a book deal.',
    description: 'Independent and self-published authors sharing craft, publishing strategy, and the occasional launch-day panic.',
    memberCount: 640, activeNow: 29, createdAt: now - 200 * DAY,
    tags: ['self-publishing', 'indie'],
    joinStatus: 'pending',
  },
  {
    id: 'worldbuilding-club', name: 'Worldbuilding Club', tone: 'sage', category: 'Writing', type: 'writer', visibility: 'public',
    tagline: 'Maps, magic systems, and the messy first drafts of entire universes.',
    description: 'For writers building the world before (or instead of) the plot. Magic systems, maps, and made-up languages welcome.',
    memberCount: 890, activeNow: 38, createdAt: now - 150 * DAY,
    tags: ['worldbuilding', 'fantasy writing'],
    joinStatus: 'none',
  },
  {
    id: 'sunday-book-club', name: 'Sunday Book Club', tone: 'coral', category: 'Reading Clubs', type: 'reader', visibility: 'public',
    tagline: 'One book a month, one long Sunday conversation about it.',
    description: 'A slower-paced monthly book club. New members always welcome — you can always catch up.',
    memberCount: 420, activeNow: 18, createdAt: now - 90 * DAY,
    currentBook: { title: 'The Night Circus', author: 'Erin Morgenstern', tone: 'coral' },
    tags: ['monthly read', 'book club'],
    joinStatus: 'none',
  },
  {
    id: 'book-to-movie', name: 'Book-to-Movie Club', tone: 'blue', category: 'Book Discussions', type: 'reader', visibility: 'public',
    tagline: 'The book was better. Let’s argue about why (or why not).',
    description: 'Comparing adaptations to the source material — casting takes, cut scenes, and everything the movie got wrong (or surprisingly right).',
    memberCount: 1340, activeNow: 66, createdAt: now - 210 * DAY,
    tags: ['adaptations', 'film club'],
    joinStatus: 'none',
  },
  {
    id: 'cosmere-community', name: 'Cosmere Community', tone: 'blue', category: 'Fandoms', type: 'reader', visibility: 'public',
    tagline: 'For readers deep in Sanderson’s interconnected universe — theories welcome, spoilers tagged.',
    description: 'Everything Cosmere: Mistborn, Stormlight, and the shared universe theories that never stop.',
    memberCount: 1920, activeNow: 101, createdAt: now - 340 * DAY,
    currentBook: { title: 'Mistborn: The Final Empire', author: 'Brandon Sanderson', tone: 'blue' },
    tags: ['cosmere', 'fandom'],
    joinStatus: 'none',
  },
];

export const MEMBERS_BY_COMMUNITY = {
  'fantasy-readers': [
    M('user_me', 'Sakshi', 'coral', 'member'),
    M('mem_1', 'Priya', 'coral', 'admin'),
    M('mem_2', 'Owen', 'sage', 'moderator'),
    M('mem_3', 'Devi', 'yellow'),
    M('mem_4', 'Jonah', 'blue'),
    M('mem_5', 'Mira', 'coral'),
    M('mem_6', 'Sam', 'sage'),
  ],
  'scifi-explorers': [
    M('user_me', 'Sakshi', 'coral', 'member'),
    M('mem_7', 'Alex', 'blue', 'admin'),
    M('mem_8', 'Noor', 'sage', 'moderator'),
    M('mem_9', 'Theo', 'yellow'),
  ],
  'fiction-writers': [
    M('user_me', 'Sakshi', 'coral', 'moderator'),
    M('mem_10', 'Wren', 'coral', 'admin'),
    M('mem_11', 'Kai', 'yellow'),
    M('mem_12', 'Ana', 'blue'),
    M('mem_13', 'Ravi', 'sage'),
  ],
  'sunday-book-club': [
    M('mem_14', 'Grace', 'coral', 'admin'),
    M('mem_15', 'Leo', 'sage'),
  ],
  'worldbuilding-club': [
    M('mem_16', 'Iris', 'sage', 'admin'),
    M('mem_17', 'Finn', 'yellow'),
  ],
  'mystery-crime': [
    M('mem_18', 'Dana', 'blue', 'admin'),
  ],
};

export const POSTS = [
  {
    id: 'post_1', communityId: 'fantasy-readers', type: 'question', pinned: true,
    author: { name: 'Priya', initial: 'P', tone: 'coral', role: 'admin' }, createdAt: now - 2 * 60 * 60 * 1000,
    title: 'What fantasy book completely changed your expectations of the genre?',
    body: "Mine was The Fifth Season — I went in expecting swords and came out reevaluating what fantasy could even be about. What's yours?",
    isSpoiler: false, likes: 214, liked: false, saved: false,
    comments: [
      { id: 'c1', author: { name: 'Owen', initial: 'O', tone: 'sage' }, body: 'Piranesi. Didn’t know a fantasy book could feel like that.', createdAt: now - 90 * 60 * 1000, isSpoiler: false },
      { id: 'c2', author: { name: 'Mira', initial: 'M', tone: 'coral' }, body: 'The Name of the Wind, no contest.', createdAt: now - 40 * 60 * 1000, isSpoiler: false },
    ],
  },
  {
    id: 'post_2', communityId: 'fantasy-readers', type: 'book_discussion',
    author: { name: 'Devi', initial: 'D', tone: 'yellow', role: 'member' }, createdAt: now - 5 * 60 * 60 * 1000,
    title: 'The Name of the Wind — Chapter 40 discussion',
    body: 'We just hit the Felurian chapters. I have thoughts and most of them are spoilers.',
    book: { title: 'The Name of the Wind', author: 'Patrick Rothfuss', tone: 'coral' },
    isSpoiler: true, likes: 58, liked: false, saved: false,
    comments: [
      { id: 'c3', author: { name: 'Sam', initial: 'S', tone: 'sage' }, body: 'This section is SO divisive lol', createdAt: now - 60 * 60 * 1000, isSpoiler: false },
    ],
  },
  {
    id: 'post_3', communityId: 'fantasy-readers', type: 'poll',
    author: { name: 'Jonah', initial: 'J', tone: 'blue', role: 'member' }, createdAt: now - 8 * 60 * 60 * 1000,
    title: 'Best fantasy villain of all time?',
    body: 'Voting closes Sunday. Defend your pick in the comments.',
    poll: { options: [{ label: 'Kelsier', votes: 142 }, { label: 'Sauron', votes: 98 }, { label: 'The Darkling', votes: 176 }, { label: 'Emperor Elend (jk)', votes: 12 }] },
    isSpoiler: false, likes: 76, liked: true, saved: false, comments: [],
  },
  {
    id: 'post_4', communityId: 'fantasy-readers', type: 'recommendation',
    author: { name: 'Mira', initial: 'M', tone: 'coral', role: 'member' }, createdAt: now - 26 * 60 * 60 * 1000,
    title: 'If you liked Mistborn, read this next',
    body: 'The Poppy War scratches the same "clever magic system, morally messy protagonist" itch, just angrier.',
    isSpoiler: false, likes: 39, liked: false, saved: true, comments: [],
  },
  {
    id: 'post_5', communityId: 'fiction-writers', type: 'writing_prompt', pinned: true,
    author: { name: 'Wren', initial: 'W', tone: 'coral', role: 'admin' }, createdAt: now - 3 * 60 * 60 * 1000,
    title: 'Weekly Prompt',
    body: 'Write a scene where two characters meet for the last time — and only one of them knows it.',
    isSpoiler: false, likes: 61, liked: false, saved: false, comments: [],
  },
  {
    id: 'post_6', communityId: 'fiction-writers', type: 'share_writing',
    author: { name: 'Kai', initial: 'K', tone: 'yellow', role: 'member' }, createdAt: now - 10 * 60 * 60 * 1000,
    title: 'Opening line — too much?',
    body: 'Would love a gut reaction before I commit to this as my chapter one.',
    excerpt: 'The last time Mara set the house on fire, it was an accident. This time, she brought matches.',
    isSpoiler: false, likes: 47, liked: false, saved: false,
    comments: [
      { id: 'c4', author: { name: 'Ana', initial: 'A', tone: 'blue' }, body: 'Keep it exactly as is. That’s a great hook.', createdAt: now - 5 * 60 * 60 * 1000, isSpoiler: false },
    ],
  },
  {
    id: 'post_7', communityId: 'fiction-writers', type: 'feedback_request',
    author: { name: 'Ravi', initial: 'R', tone: 'sage', role: 'member' }, createdAt: now - 30 * 60 * 60 * 1000,
    title: 'Does this dialogue feel natural to anyone else, or just me?',
    body: 'Second-guessing an entire scene of banter. Brutal honesty welcome.',
    isSpoiler: false, likes: 22, liked: false, saved: false, comments: [],
  },
  {
    id: 'post_8', communityId: 'sunday-book-club', type: 'reading_update',
    author: { name: 'Grace', initial: 'G', tone: 'coral', role: 'admin' }, createdAt: now - 4 * 60 * 60 * 1000,
    title: 'Halfway through The Night Circus and I am NOT okay',
    body: 'The rêveurs chapter destroyed me. See everyone Sunday.',
    book: { title: 'The Night Circus', author: 'Erin Morgenstern', tone: 'coral' },
    isSpoiler: false, likes: 33, liked: false, saved: false, comments: [],
  },
  {
    id: 'post_9', communityId: 'sunday-book-club', type: 'announcement', pinned: true,
    author: { name: 'Grace', initial: 'G', tone: 'coral', role: 'admin' }, createdAt: now - 48 * 60 * 60 * 1000,
    title: 'October pick is live',
    body: 'We’re reading The Night Circus this month — grab a copy and join us Sunday at 7pm.',
    isSpoiler: false, likes: 18, liked: false, saved: false, comments: [],
  },
  {
    id: 'post_10', communityId: 'mystery-crime', type: 'review',
    author: { name: 'Dana', initial: 'D', tone: 'blue', role: 'admin' }, createdAt: now - 14 * 60 * 60 * 1000,
    title: 'The Silent Patient — the twist actually works',
    body: 'I usually see these coming. Did not see this coming. Full spoiler review below.',
    book: { title: 'The Silent Patient', author: 'Alex Michaelides', tone: 'blue' },
    isSpoiler: true, likes: 88, liked: false, saved: false, comments: [],
  },
  {
    id: 'post_11', communityId: 'worldbuilding-club', type: 'writing_update',
    author: { name: 'Iris', initial: 'I', tone: 'sage', role: 'admin' }, createdAt: now - 20 * 60 * 60 * 1000,
    title: 'Finally drew the map for my three-moon world',
    body: 'Took me four attempts to make the tides make sense. Happy to share the process if anyone’s curious.',
    isSpoiler: false, likes: 54, liked: false, saved: false, comments: [],
  },
  {
    id: 'post_12', communityId: 'scifi-explorers', type: 'discussion',
    author: { name: 'Alex', initial: 'A', tone: 'blue', role: 'admin' }, createdAt: now - 6 * 60 * 60 * 1000,
    title: 'Hard sci-fi that actually gets the physics right?',
    body: 'Project Hail Mary aside — what else should be on this list?',
    isSpoiler: false, likes: 41, liked: false, saved: false, comments: [],
  },
];

export const EVENTS = [
  {
    id: 'event_1', communityId: 'sunday-book-club', title: 'Sunday Book Club',
    book: { title: 'The Night Circus', author: 'Erin Morgenstern', tone: 'coral' },
    datetime: now + 3 * DAY, host: { name: 'Grace', initial: 'G', tone: 'coral' },
    interested: 128, joined: false,
  },
  {
    id: 'event_2', communityId: 'fantasy-readers', title: 'August Fantasy Read-Along',
    book: { title: 'The Name of the Wind', author: 'Patrick Rothfuss', tone: 'coral' },
    datetime: now + 9 * DAY, host: { name: 'Priya', initial: 'P', tone: 'coral' },
    interested: 342, joined: true,
  },
  {
    id: 'event_3', communityId: 'fiction-writers', title: 'Author Q&A: Drafting Without Dying Inside',
    host: { name: 'Wren', initial: 'W', tone: 'coral' },
    datetime: now + 6 * DAY, interested: 96, joined: false,
  },
  {
    id: 'event_4', communityId: 'mystery-crime', title: 'Midnight Mystery Marathon',
    host: { name: 'Dana', initial: 'D', tone: 'blue' },
    datetime: now + 14 * DAY, interested: 61, joined: false,
  },
];

export const CHALLENGES = [
  {
    id: 'challenge_1', communityId: 'classic-literature', title: 'Read 5 Classics This Season',
    description: 'One classic every three weeks — no rereads, no cheating with novellas.',
    participantCount: 312, goal: 5, progress: 2, endsAt: now + 40 * DAY,
    leaderboard: [{ name: 'Elena', count: 5 }, { name: 'Marcus', count: 4 }, { name: 'You', count: 2 }],
  },
  {
    id: 'challenge_2', communityId: 'fiction-writers', title: 'Women Writers Challenge',
    description: 'Finish a full draft while reading five books by women writers for inspiration.',
    participantCount: 148, goal: 5, progress: 3, endsAt: now + 20 * DAY,
    leaderboard: [{ name: 'Wren', count: 5 }, { name: 'You', count: 3 }, { name: 'Kai', count: 2 }],
  },
];

export const NOTIFICATIONS = [
  { id: 'n1', type: 'reply', text: 'Owen replied to your post in Fantasy Readers', createdAt: now - 20 * 60 * 1000, unread: true },
  { id: 'n2', type: 'mention', text: 'Ana mentioned you in Fiction Writers', createdAt: now - 90 * 60 * 1000, unread: true },
  { id: 'n3', type: 'discussion', text: 'New discussion in Sci-Fi Explorers', createdAt: now - 5 * 60 * 60 * 1000, unread: true },
  { id: 'n4', type: 'event', text: 'August Fantasy Read-Along starts in 2 days', createdAt: now - 8 * 60 * 60 * 1000, unread: false },
  { id: 'n5', type: 'join_accepted', text: 'Your request to join Indie Writers is still pending', createdAt: now - 24 * 60 * 60 * 1000, unread: false },
  { id: 'n6', type: 'announcement', text: 'Grace posted an announcement in Sunday Book Club', createdAt: now - 30 * 60 * 60 * 1000, unread: false },
];

export const CHAT_MESSAGES_BY_COMMUNITY = {
  'fantasy-readers': [
    { id: 'm1', author: { name: 'Owen', initial: 'O', tone: 'sage' }, text: 'anyone else just get to the Felurian chapters 👀', createdAt: now - 12 * 60 * 1000 },
    { id: 'm2', author: { name: 'Mira', initial: 'M', tone: 'coral' }, text: 'NO SPOILERS I’m two chapters behind', createdAt: now - 10 * 60 * 1000 },
    { id: 'm3', author: { name: 'Devi', initial: 'D', tone: 'yellow' }, text: 'lol sorry. it’s a lot', createdAt: now - 8 * 60 * 1000 },
    { id: 'm4', author: { name: 'Jonah', initial: 'J', tone: 'blue' }, text: 'currently reading The Name of the Wind for the third time, no regrets', createdAt: now - 3 * 60 * 1000 },
  ],
  'fiction-writers': [
    { id: 'm5', author: { name: 'Wren', initial: 'W', tone: 'coral' }, text: 'reminder: critique thread closes tonight', createdAt: now - 40 * 60 * 1000 },
    { id: 'm6', author: { name: 'Kai', initial: 'K', tone: 'yellow' }, text: 'submitting mine in 10 min, cutting it close as always', createdAt: now - 15 * 60 * 1000 },
  ],
};
